<script setup lang="ts">
//#region import
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSessionAction } from '@/composables/useSessionAction';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import type { GetControlTowerCustomerDomainsResponse } from '@/types/openapi-ts';
import type { UiTableNewField } from '@/types/table';
//#endregion

//#region const
const fields: UiTableNewField<GetControlTowerCustomerDomainsResponse['payload'][number]>[]  = [
  {
    name: 'country_code',
    minWidth: '259',
    align: 'left',
    renderValue: (_, row) => `<div style="display: flex">${countriesIndex.value[row.country_code]!.name}${
      row.is_default
        ? `<div class="system-domains__table-tag"><i class="fas fa-home"></i>${t('settings.systemDomains.default')}</div>` 
        : ''
      }</div>`,
  },
  {
    name: 'front_domain',
    minWidth: '250',
    align: 'left',
    copyValue: true,
  },
  {
    name: 'panel_domain',
    minWidth: '250',
    align: 'left',
    copyValue: true,
  },
  {
    name: 'admin_domain',
    minWidth: '250',
    align: 'left',
    copyValue: true,
  },
];
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const store = useTypedStore();
const { onSessionUnlock } = useSessionAction();
const popupManager = usePopupManager();
//#endregion

//#region data
const tableData = ref<GetControlTowerCustomerDomainsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const countriesIndex = computed(() => store.getters['misc/countriesIndex']);

const selectedCountries = computed(() => {
  return tableData.value.map(({ country_code }) => country_code);
});
//#endregion

//#region methods
const initData = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  return api.getCustomerDomains().then((res) => {
    tableData.value = res.data.payload;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};

const handleMakeDefault = (row: GetControlTowerCustomerDomainsResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.systemDomains.confirm.setAsDefaultDomains'),
      message: t(
        'settings.systemDomains.confirm.confirmSetAsDefault', 
        { 
          country_name: countriesIndex.value[row.country_code]!.name,
        },
      ),
      bodyIcon: 'house-circle-check',
      bodyIconColor: 'var(--text-positive)',
      confirmTitle: t('settings.systemDomains.confirm.setAsDefault'),
      confirmType: 'positive',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.putCustomerDomainsSetDefault(row);
          await initData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
};
const handleDelete = (row: GetControlTowerCustomerDomainsResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.systemDomains.confirm.deleteSystemDomains'),
      message: t(
        'settings.systemDomains.confirm.confirmDelete', 
        { 
          country_name: countriesIndex.value[row.country_code]!.name, 
        },
      ),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('settings.tokens.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteCustomerDomains(row);
          await initData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
};

const handleAddDomainsPopup = () => {
  popupManager.addSystemDomain.open({
    data: {
      selectedCountries: selectedCountries.value,
    },
    listeners: {
      success: () => {
        initData();
      },
    },
  });
};

const handleEdit = (row: GetControlTowerCustomerDomainsResponse['payload'][number]) => {
  popupManager.addSystemDomain.open({
    data: {
      popupData: row,
      selectedCountries: selectedCountries.value,
    },
    listeners: {
      success: () => {
        initData();
      },
    },
  });
};
//#endregion

//#region hooks
onSessionUnlock.value = initData;
initData();
//#endregion
</script>

<template>
  <div class="system-domains">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.systemDomains.title') }}
      </template>
      <template v-slot:actions>
        <UiButton
          filled
          icon="plus"
          @click="handleAddDomainsPopup"
        >
          {{ $t('settings.systemDomains.confirm.addSystemDomains') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      disable-sort
      is-static
      lazy-loading
      i18n-path="settings.systemDomains"
      element-loading-custom-class="data-loader-spinner"
      class="system-domains__table"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="100"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="house-circle-check"
              color="var(--text-primary)"
              :tooltip="$t('settings.systemDomains.confirm.setAsDefault')"
              :disabled="scope.row.is_default"
              @click="handleMakeDefault(scope.row)"
            />
            <ActionIcon
              icon="pen"
              color="var(--text-primary)"
              :tooltip="$t('crm.buttons.edit')"
              @click="handleEdit(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              :disabled="scope.row.is_default"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang='scss'>
.system-domains {
  &__table-tag{
    border-radius: 8px;
    background-color: var(--background-positive);
    padding: 0 4px;
    height: 16px;
    margin-left: 4px;
    color: #fff;
    font-size: 10px;
    font-weight: 500;
    i {
      margin-right: 4px;
    }
  }
}
</style>
